// Port of ios/Sources/ViewModels/MessageAttributedText.swift, plus the pure parts of
// ios/Sources/Views/MessageBody.swift (`embeddedImageSpans`, `customEmojiSpans`, and the
// placement ordering of its `composed` layout pass).
//
// The pure half of rendering a message body: `InlineMarkdown` spans, emoji-shortcode
// substitution, mention highlighting and `~channel` links, folded into styled text.
//
// Lifted out of `MessageBody` so BOTH Apple clients share it. The view that draws the result
// differs per platform — iOS interleaves UIKit images for custom emoji and embedded pictures,
// macOS does the same with AppKit, the web with React — but which characters are bold, which
// are a link, and where a mention starts must never be answered twice.
//
// WEB: Swift folds everything into an `AttributedString`. Here the same decisions come out as
// plain data: the rendered `text` cut into `runs`, each run one stretch of identical
// attributes, so a renderer walks the runs and emits one element per run. Offsets are UTF-16
// code units (Swift: Characters) — `text.slice(run.start, run.end) === run.text`.
//
// Swift's `mentionColor: Color?` switch is not carried over: with no `knownUsernames`, no
// `currentUsername` and no `knownChannelSlugs` (the only way Swift's plain `attributed(message)`
// calls it) `MentionHighlight` and `ChannelLinks` find nothing anyway, so passing no sets IS the
// "no highlighting" form. Colours are the renderer's business; the run says WHAT it is.

import { EmojiShortcodes, type EmojiShortcodeSpan } from './emojiShortcodes';
import { InlineMarkdown, type InlineSpan, type ParsedText } from './inlineMarkdown';
import { ChannelLinks } from './channelLinks';
import { MentionHighlight } from './mentionHighlight';
import { MentionDisplay } from './mentionDisplay';

export interface MessageTextOptions {
  /** Empty = no mention highlighting (headers, previews). Callers rendering conversation
   *  messages pass the loaded page's author usernames. */
  knownUsernames?: ReadonlySet<string>;
  currentUsername?: string | null;
  /** The open team's channel slugs — what `~general` may link against. */
  knownChannelSlugs?: ReadonlySet<string>;
  /**
   * Lowercased username → full name (`TeammateNameDisplay.fullName`). A highlighted mention of
   * someone in it is DRAWN `@Full Name` (`MentionDisplay`). Opt-in: only posted-message display
   * passes it — the composer and editors must keep `@username`, the text that gets sent.
   */
  mentionFullNames?: ReadonlyMap<string, string>;
}

export interface MessageTextRun {
  /** UTF-16 offsets into `MessageText.text`. */
  start: number;
  end: number;
  text: string;
  /** Swift `.stronglyEmphasized`: a bold span OR a mention (mentions are drawn strong too). */
  bold: boolean;
  /** Swift `.emphasized`. */
  italic: boolean;
  /** Swift: monospaced body font. */
  code: boolean;
  strikethrough: boolean;
  /**
   * The tap target. A mention carries `satori://user/<username>`, a channel link
   * `satori://channel/<slug>` — the containing screen routes those (open the DM / switch
   * channel). A Markdown link's URL wins over either where they overlap, as in Swift, where
   * the span pass runs last and overwrites `.link`.
   */
  link: string | null;
  /** Set on a highlighted @mention: draw it in the accent colour; `isSelf` adds the soft
   *  field behind your own name. */
  mention: { username: string; isSelf: boolean } | null;
  /** Set on a highlighted `~slug`: draw it in the accent colour. */
  channelSlug: string | null;
  /** Set on the single U+FFFC placeholder of an embedded `![alt](url)` image. */
  image: { url: string; alt: string | null } | null;
}

export interface MessageText {
  /** The rendered text (shortcodes substituted, Markdown syntax stripped, mentions of people in
   *  `mentionFullNames` drawn as their full name). */
  text: string;
  runs: MessageTextRun[];
  /** The inline parse the runs came from — in the coordinates BEFORE mention names were
   *  substituted (`parsed.text` differs from `text` only when a name was drawn). */
  parsed: ParsedText;
}

interface Overlay {
  start: number;
  end: number;
  apply: (run: MessageTextRun) => void;
}

function attributed(message: string, options: MessageTextOptions = {}): MessageText {
  const knownUsernames = options.knownUsernames ?? new Set<string>();
  const currentUsername = options.currentUsername ?? null;
  const knownChannelSlugs = options.knownChannelSlugs ?? new Set<string>();

  // Standard shortcodes become characters BEFORE the parse, so offsets everywhere below
  // are measured against the text the reader actually sees.
  const substituted = EmojiShortcodes.substituteStandard(message);
  const parsed = InlineMarkdown.parse(substituted);
  const found = MentionHighlight.spans({ text: parsed.text, knownUsernames, currentUsername });
  // Mentions drawn as full names change the text's length; every other overlay below was
  // measured against `parsed.text` and is carried across by `mapStart` / `mapEnd`. With no names
  // map this is the identity and `text === parsed.text`.
  const display = MentionDisplay.rewrite(
    parsed.text,
    found,
    options.mentionFullNames ?? new Map(),
    parsed.spans.filter((s) => s.style === 'code'),
  );
  const text = display.text;
  const overlays: Overlay[] = [];

  for (const mention of display.mentions) {
    overlays.push({
      start: mention.start,
      end: mention.end,
      apply: (run) => {
        run.bold = true;
        // Tapping a mention opens that person — routed by the screen. Always the USERNAME, even
        // when the full name is what is drawn.
        run.link = `satori://user/${mention.username}`;
        // Your own name gets a soft field behind it: the one call-out worth an extra
        // signal, and only that one.
        run.mention = { username: mention.username, isSelf: mention.isSelf };
      },
    });
  }
  // Channel links overlay the rendered text exactly as mentions do: not markup, just
  // known slugs lighting up.
  for (const span of ChannelLinks.spans(parsed.text, knownChannelSlugs)) {
    overlays.push({
      start: display.mapStart(span.start),
      end: display.mapEnd(span.end),
      apply: (run) => {
        run.link = `satori://channel/${span.slug}`;
        run.channelSlug = span.slug;
      },
    });
  }
  for (const span of parsed.spans) {
    if (!(span.start >= 0 && span.start < span.end && span.end <= parsed.text.length)) continue;
    overlays.push({ start: display.mapStart(span.start), end: display.mapEnd(span.end), apply: (run) => applyStyle(run, span) });
  }
  return { text, runs: cut(text, overlays), parsed };
}

function applyStyle(run: MessageTextRun, span: InlineSpan): void {
  switch (span.style) {
    case 'bold':
      run.bold = true;
      break;
    case 'italic':
      run.italic = true;
      break;
    case 'code':
      run.code = true;
      break;
    case 'strikethrough':
      run.strikethrough = true;
      break;
    case 'link':
      // Swift only sets `.link` when `URL(string:)` accepts the text; the browser decides
      // that at click time here, so any non-empty URL is carried.
      if (span.url) run.link = span.url;
      break;
    case 'image':
      // Nothing to style: the picture IS the span, swapped in at layout. It carries no
      // link on purpose — tapping an avatar the bridge stamped on every message should
      // reply to the message, not open a JPEG in a new tab.
      if (span.url) run.image = { url: span.url, alt: span.alt ?? null };
      break;
  }
}

/** Cut `text` at every overlay boundary and apply the overlays IN ORDER to each piece. Bold
 *  inside italic keeps both (Swift's `mergeIntent`): flags only ever turn on. */
function cut(text: string, overlays: readonly Overlay[]): MessageTextRun[] {
  if (text === '') return [];
  const bounds = new Set<number>([0, text.length]);
  for (const o of overlays) {
    bounds.add(o.start);
    bounds.add(o.end);
  }
  const sorted = [...bounds].sort((a, b) => a - b);
  const runs: MessageTextRun[] = [];
  for (let k = 0; k + 1 < sorted.length; k++) {
    const start = sorted[k];
    const end = sorted[k + 1];
    const run = plainRun(text, start, end);
    for (const o of overlays) if (o.start <= start && end <= o.end) o.apply(run);
    const previous = runs[runs.length - 1];
    if (previous && sameAttributes(previous, run)) {
      previous.end = end;
      previous.text = text.slice(previous.start, end);
    } else {
      runs.push(run);
    }
  }
  return runs;
}

function plainRun(text: string, start: number, end: number): MessageTextRun {
  return {
    start,
    end,
    text: text.slice(start, end),
    bold: false,
    italic: false,
    code: false,
    strikethrough: false,
    link: null,
    mention: null,
    channelSlug: null,
    image: null,
  };
}

function sameAttributes(a: MessageTextRun, b: MessageTextRun): boolean {
  return (
    a.bold === b.bold &&
    a.italic === b.italic &&
    a.code === b.code &&
    a.strikethrough === b.strikethrough &&
    a.link === b.link &&
    a.mention?.username === b.mention?.username &&
    a.mention?.isSelf === b.mention?.isSelf &&
    a.channelSlug === b.channelSlug &&
    // Two adjacent images are two pictures, never one run.
    a.image === null &&
    b.image === null
  );
}

/**
 * The runs covering `[start, end)`, clipped to it — Swift's `characterRange` +
 * `attributed[range]`, which the layout pass slices with. Out-of-bounds clips rather than
 * throws: a message must render regardless.
 */
function slice(message: MessageText, start: number, end: number): MessageTextRun[] {
  const lo = Math.max(0, start);
  const hi = Math.min(message.text.length, end);
  const out: MessageTextRun[] = [];
  if (lo >= hi) return out;
  for (const run of message.runs) {
    if (run.end <= lo || run.start >= hi) continue;
    const s = Math.max(run.start, lo);
    const e = Math.min(run.end, hi);
    out.push({ ...run, start: s, end: e, text: message.text.slice(s, e) });
  }
  return out;
}

// MARK: - MessageBody's pure parts

/**
 * The custom-emoji shortcodes left in the RENDERED text, if any could be there at all.
 *
 * Two guards before the expensive part, in the order that costs least: an empty catalogue
 * (a server with no custom emoji, or one that has not synced yet) can match nothing, and a
 * body with no colon in it cannot hold a `:name:`. Only past both is the rendered string
 * scanned. The placeholder is the `:name:` text itself, the desktop's own fallback.
 */
function customEmojiSpans(renderedText: string, names: ReadonlySet<string>): EmojiShortcodeSpan[] {
  if (names.size === 0) return [];
  if (!renderedText.includes(':')) return [];
  return EmojiShortcodes.spansMatching(renderedText, names);
}

/**
 * The `![alt](url)` occurrences in this body, in the parser's coordinate space — the same one
 * `attributed` produces, so the offsets line up with the rendered string.
 *
 * Guarded by a substring test so the overwhelming majority of messages, which embed nothing,
 * never pay for a second parse. The ones that do are almost all WhatsApp-bridged: the bridge
 * opens every relayed message with the sender's avatar as image Markdown. (WEB: the runs from
 * `attributed` already carry `image`; this is for a caller that needs the list up front, e.g.
 * to start the fetches.)
 */
function embeddedImageSpans(message: string): InlineSpan[] {
  if (!message.includes('![')) return [];
  const substituted = EmojiShortcodes.substituteStandard(message);
  return InlineMarkdown.parse(substituted).spans.filter((span) => span.style === 'image' && (span.url ?? '') !== '');
}

/**
 * One placement in the inline walk. Custom emoji and embedded images both interrupt the run
 * of text with a picture, so they are placed in ONE ordered pass — two passes would each have
 * to know about the other's cursor, and the second would re-slice what the first rebuilt.
 */
export interface InlinePlacement {
  start: number;
  end: number;
  emojiName: string | null;
  imageURL: string | null;
}

/**
 * The placements of `MessageBody.composed`, in drawing order. The renderer then emits
 * `slice(text, cursor, placement.start)`, the picture (a custom emoji not loaded yet, or
 * unloadable, keeps its `:name:` text — never a hole; an embedded image keeps a fixed-size
 * tile, loaded or not, so the line does not jump when the bytes land), and so on, then the tail.
 */
function inlinePlacements(
  emojiSpans: readonly EmojiShortcodeSpan[],
  imageSpans: readonly InlineSpan[],
): InlinePlacement[] {
  const all: InlinePlacement[] = [
    ...emojiSpans.map((s) => ({ start: s.start, end: s.end, emojiName: s.name, imageURL: null })),
    ...imageSpans.map((s) => ({ start: s.start, end: s.end, emojiName: null, imageURL: s.url ?? null })),
  ].sort((a, b) => a.start - b.start);
  const out: InlinePlacement[] = [];
  let cursor = 0;
  for (const placement of all) {
    // Defensive, and cheap: an overlapping or inverted placement would slice backwards.
    if (!(placement.start >= cursor && placement.start < placement.end)) continue;
    out.push(placement);
    cursor = placement.end;
  }
  return out;
}

export const MessageAttributedText = {
  attributed,
  slice,
  customEmojiSpans,
  embeddedImageSpans,
  inlinePlacements,
} as const;
